import { HttpErrorResponse } from '@angular/common/http';

import { ORBI_ERROR_TEXT } from '@patient-ui/shared/constants';
import { OrbiProblemDetails } from '@patient-ui/shared/models';

/**
 * Normalized, detectable error condition for Orbi requests (satisfies
 * PCSA-13163 AC: "connection or authentication failures return a detectable
 * error condition that the web client can handle").
 */
export enum OrbiErrorKind {
  /** No network / request never completed (offline, DNS, CORS, timeout). */
  Connection = 'connection',
  /** 401/403 — token rejected or unauthorized. */
  Auth = 'auth',
  /** 400 / other 4xx with a ProblemDetails body. */
  BadRequest = 'badRequest',
  /** 503/504 (and other 5xx) — transient; safe to offer retry. */
  Transient = 'transient',
  /** Anything else. */
  Unknown = 'unknown',
}

export interface OrbiError {
  kind: OrbiErrorKind;
  status: number;
  /** True for conditions where an immediate retry is reasonable. */
  retryable: boolean;
  /** Human-readable detail, preferring the gateway's ProblemDetails.detail. */
  message: string;
  /** Parsed RFC 7807 body when present. */
  problem?: OrbiProblemDetails;
}

/**
 * Maps a thrown HttpClient error into a typed [OrbiError].
 *
 * Transient handling covers BOTH 503 and 504 — mobile only special-cased 503,
 * but the OpenAPI spec documents 504 (Gateway Timeout) on `/orbi-agent`, so the
 * web client treats both (and other 5xx) as retryable. See
 * docs/orbi/moonshot-api-reference.md §4 (P1).
 */
export function mapOrbiError(error: unknown): OrbiError {
  if (!(error instanceof HttpErrorResponse)) {
    return {
      kind: OrbiErrorKind.Unknown,
      status: 0,
      retryable: false,
      message: 'An unexpected error occurred.',
    };
  }

  const status = error.status;
  const problem = extractProblemDetails(error);
  const detail = problem?.detail || problem?.title || error.message;

  // status 0 => request never reached the server (offline / CORS / timeout).
  if (status === 0) {
    return {
      kind: OrbiErrorKind.Connection,
      status,
      retryable: true,
      message: 'Unable to reach the service. Please check your connection.',
      problem,
    };
  }

  if (status === 401 || status === 403) {
    return {
      kind: OrbiErrorKind.Auth,
      status,
      retryable: false,
      message: detail || 'You are not authorized to use this service.',
      problem,
    };
  }

  if (status >= 500) {
    return {
      kind: OrbiErrorKind.Transient,
      status,
      retryable: true,
      message: detail || 'The service is temporarily unavailable.',
      problem,
    };
  }

  if (status >= 400) {
    return {
      kind: OrbiErrorKind.BadRequest,
      status,
      retryable: false,
      message: detail || 'The request could not be processed.',
      problem,
    };
  }

  return {
    kind: OrbiErrorKind.Unknown,
    status,
    retryable: false,
    message: detail || 'An unexpected error occurred.',
    problem,
  };
}

/**
 * The user-facing error experience for a failed agent turn (PCSA-13172):
 * friendly, non-technical copy (never the raw `error.message` / ProblemDetails
 * detail, which may carry backend language — AC 2) plus whether a retry
 * affordance should be offered. Maps each supported status (AC 6) to the right
 * message (AC 7/8/9); anything unrecognized degrades to the generic retryable
 * message (AC 6 fallback, AC 4 — never locks the experience).
 */
export interface OrbiUserFacingError {
  text: string;
  /** Show a "Try again" affordance (AC 3/10) — resends the failed request. */
  canRetry: boolean;
}

export function getOrbiUserFacingError(error: OrbiError): OrbiUserFacingError {
  // 401 (session/token) and 403 (forbidden) both show the non-technical access
  // message WITHOUT exposing auth/authz detail (AC 8). Portal session expiry
  // stays owned by the existing Portal timeout modal (AC 11) — the agent does
  // not raise a competing timeout message.
  if (error.status === 401 || error.status === 403) {
    return { text: ORBI_ERROR_TEXT.forbidden, canRetry: false };
  }
  // Conversation/session not found — path forward is "start a new conversation"
  // (AC 9), not a resend.
  if (error.status === 404) {
    return { text: ORBI_ERROR_TEXT.notFound, canRetry: false };
  }
  // No network / request never completed (offline / CORS / timeout).
  if (error.status === 0) {
    return { text: ORBI_ERROR_TEXT.connection, canRetry: true };
  }
  // 5xx (500/503/504 and other server errors) → temporarily unavailable (AC 7).
  if (error.status >= 500) {
    return { text: ORBI_ERROR_TEXT.serverUnavailable, canRetry: true };
  }
  // Other 4xx → bad request (AC 6 general 4xx).
  if (error.status >= 400) {
    return { text: ORBI_ERROR_TEXT.badRequest, canRetry: true };
  }
  // Unrecognized / fallback error (AC 6): retryable by default, never locks.
  return { text: ORBI_ERROR_TEXT.unknown, canRetry: error.retryable };
}

/** Best-effort parse of an RFC 7807 ProblemDetails body. */
function extractProblemDetails(
  error: HttpErrorResponse
): OrbiProblemDetails | undefined {
  const body = error.error;
  if (body && typeof body === 'object' && !(body instanceof ProgressEvent)) {
    return body as OrbiProblemDetails;
  }
  if (typeof body === 'string') {
    try {
      const parsed = JSON.parse(body);
      if (parsed && typeof parsed === 'object') {
        return parsed as OrbiProblemDetails;
      }
    } catch {
      // not JSON — ignore
    }
  }
  return undefined;
}
